import { msg } from "@lingui/core/macro";
import {
  createOrderMutation,
  createTableMutation,
  type FloorCell,
  getTablesQuery,
  queryKeys,
  setTablePositionMutation,
  setTableStatusMutation,
} from "@comandero/shared";
import type { Branch, DiningTable, Organization } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createSignal, For, Show } from "solid-js";
import { useNavigate } from "@/lib/u-router";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { FloorMap } from "./floor-map";

interface FloorManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

export function FloorManager(props: FloorManagerProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const [tableName, setTableName] = createSignal("");
  const [editingLayout, setEditingLayout] = createSignal(false);
  let tableNameInput: HTMLInputElement | undefined;

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const tables = createPowerSyncQuery<DiningTable>({
    queryKey: () => queryKeys.comandero.tables(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orgId();
      const bid = branchId();
      return d && oid && bid ? getTablesQuery(d, oid, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  async function addTable(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const oid = orgId();
    const bid = branchId();
    if (!d || !oid || !bid || !tableName().trim()) {
      return;
    }
    await createTableMutation(d, oid, bid, tableName(), { seats: 4 });
    setTableName("");
  }

  async function openTable(table: DiningTable) {
    const d = drizzle();
    const oid = orgId();
    const bid = branchId();
    if (!d || !oid || !bid) {
      return;
    }
    // Open an order assigned to the table and mark it occupied, then deep-link to
    // the addressable POS ticket (#9 `?order=`). Abrir mesa lleva directo al
    // ticket compartible por URL, en vez de dejar al usuario en /floor.
    const orderId = await createOrderMutation(d, oid, bid, { type: "dine_in", tableId: table.id });
    await setTableStatusMutation(d, table.id, "occupied");
    void navigate({ to: "/pos", search: { order: orderId } });
  }

  async function freeTable(table: DiningTable) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await setTableStatusMutation(d, table.id, "free");
  }

  /** Drop del drag / flecha de teclado en el plano: persiste la celda. */
  function moveTable(tableId: string, cell: FloorCell) {
    const d = drizzle();
    if (!d) {
      return;
    }
    void setTablePositionMutation(d, tableId, cell.x, cell.y);
  }

  return (
    <div class="flex flex-col gap-6" data-testid="floor-manager">
      <form class="flex gap-2" onSubmit={addTable}>
        <TextField
          ref={tableNameInput}
          data-testid="table-name-input"
          placeholder={t(msg`Table name/number`)}
          value={tableName()}
          onInput={(e) => setTableName(e.currentTarget.value)}
        />
        <Button data-testid="add-table-btn" type="submit">
          {t(msg`Add table`)}
        </Button>
      </form>

      <Show
        when={(tables.data ?? []).length > 0}
        fallback={
          <EmptyState
            data-testid="floor-empty"
            illustration="tables"
            title={t(msg`No tables yet`)}
            description={t(msg`Add your first table to start seating guests.`)}
            onIconActivate={() => tableNameInput?.focus()}
            iconActionLabel={t(msg`Add your first table`)}
          />
        }
      >
        {/* ── Plano del salón (floor-editor) ─────────────────────────────── */}
        <section class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-medium text-muted-foreground">{t(msg`Floor plan`)}</h2>
            <Button
              size="sm"
              variant={editingLayout() ? "default" : "outline"}
              data-testid="floor-edit-toggle"
              aria-pressed={editingLayout()}
              onClick={() => setEditingLayout((v) => !v)}
            >
              {editingLayout() ? t(msg`Done`) : t(msg`Edit layout`)}
            </Button>
          </div>
          <FloorMap
            tables={() => tables.data ?? []}
            editing={editingLayout}
            onMove={moveTable}
            onSelect={(table) => {
              if (table.status === "free") {
                void openTable(table);
              }
            }}
          />
          <Show when={editingLayout()}>
            <p class="text-xs text-muted-foreground" data-testid="floor-edit-hint">
              {t(msg`Drag tables to arrange your floor. Arrow keys work too.`)}
            </p>
          </Show>
        </section>

        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          <For each={tables.data}>
            {(table) => (
              <div
                class="rounded-md border border-border/40 p-4 text-center"
                data-testid="table-card"
                data-table-name={table.name}
                data-table-status={table.status}
              >
                <div class="font-medium">{table.name}</div>
                <div
                  class="mb-2 text-xs"
                  classList={{
                    "text-green-600": table.status === "free",
                    "text-orange-500": table.status === "occupied",
                  }}
                  data-testid="table-status"
                >
                  {table.status === "free" ? t(msg`Free`) : t(msg`Occupied`)}
                </div>
                <Show
                  when={table.status === "free"}
                  fallback={
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="free-table-btn"
                      onClick={() => void freeTable(table)}
                    >
                      {t(msg`Free up`)}
                    </Button>
                  }
                >
                  <Button
                    size="sm"
                    data-testid="open-table-btn"
                    onClick={() => void openTable(table)}
                  >
                    {t(msg`Open`)}
                  </Button>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
